import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Alert, Button, Stack, Typography,
} from "@mui/material";
import { Output } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";

import { MindsetInsightSkeleton } from "./MindsetInsightSkeleton";
import { MindsetSegmentCard, useActivePage } from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { InsightLayout } from "@/components/layouts";
import { MindsetPenPortraitView } from "@/components/molecules";
import { InsightMetaProvider, useAudienceContext } from "@/context";
import { useInsight } from "@/hooks";
import { AudienceSegment, TabOption } from "@/types";
import { defaultSegment, getAudienceBreadcrumb } from "@/utils";


const MindsetInsightPage: React.FC = () => {

  let { audienceId } = useParams<{ audienceId: string }>();
  const { audience, insight, loading, error } = useInsight(audienceId);
  const { state, dispatch } = useAudienceContext();
  const navigate = useNavigate();

  const [selectedSegment, setSelectedSegment] = useState<AudienceSegment>(defaultSegment);
  const [segmentNum, setSegmentNum] = useState<number>(0);

  useEffect(() => {
    if (state.currentSegment) {
      setSelectedSegment(state.currentSegment)
      audience && setSegmentNum(audience.segments.map(x => x.segment_id).indexOf(state.currentSegment.segment_id));
    } else {
      audience && setSelectedSegment(audience.segments[0]);
      setSegmentNum(0);
    }
  }, [audience, state]);

  const handleSegmentSelect = (segment: AudienceSegment) => {
    if (audience) {
      setSelectedSegment(segment);
      dispatch({ type: 'CHANGE_SEGMENT', payload: segment });
      setSegmentNum(audience.segments.map(x => x.segment_id).indexOf(segment.segment_id));
    }
  };

  const mindsetInsightTab: TabOption = {
    showScores: true,
    showLevels: false,
    showSearch: false,
    showSorts: false,
    showDownload: false,
  };

  const handleUpdateDescription = async (text: string) => {
    return Promise.resolve();
  };

  const handleRefreshDescription = async () => {
    return insight?.meta[segmentNum].description_short;
  }

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<Output />}
        onClick={() => navigate("../export", { relative: "path" })}
      >
        Export
      </Button>
    </Stack>
  );

  return (
    <PageContainer
      actions={actionButtons}
      loading={loading}
      loadingText={"Loading Mindset Insight"}
    >
      {loading ? (
        <MindsetInsightSkeleton />
      ) : (
        <Grid container spacing={2} flexDirection="column" flexGrow={1} pb={8}>
          {error && (
            <Grid>
              <Alert variant="filled" severity="error">
                {error}
              </Alert>
            </Grid>
          )}
          {audience && (
            <>
              {/* Mindset Name */}
              <Grid size={12}>
                <Typography variant="h6">
                  {audience.audience_name}
                </Typography>
              </Grid>

              {/* Segment Cards */}
              <Grid container size={12} spacing={1}>
                {audience.segments.map((segment, index) => (
                  <Grid key={index}>
                    <MindsetSegmentCard
                      segment={segment}
                      segmentNum={segmentNum}
                      onSelect={handleSegmentSelect}
                      selected={segment.segment_id === selectedSegment.segment_id}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Segment Insight */}
              {insight &&
                <InsightLayout
                  tab={mindsetInsightTab}
                  title={selectedSegment.segment_name}
                  summary={audience.audience_size && audience.audience_size[segmentNum]}
                  onDownload={() => null}
                >
                  <InsightMetaProvider
                    handleUpdateDescription={handleUpdateDescription}
                    handleRefreshDescription={handleRefreshDescription}
                  >
                    <MindsetPenPortraitView
                      mindset={selectedSegment}
                      meta={insight.meta[segmentNum]}
                      insights={insight.insights[segmentNum]}
                      pen_portrait={insight.pen_portraits[segmentNum]}
                      locations={insight.locations[segmentNum]}
                      editable={false}
                    />
                  </InsightMetaProvider>
                </InsightLayout>
              }
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default MindsetInsightPage;
